import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    ElementRef,
    Input,
    NgModule,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewContainerRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { getBuildIdQuery, IFilterPageLayout, IFilterPageResponse, IFilterParams } from '@ncg/data';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { FavoritesPageLinkComponent } from '../../$lazy-content/components/favorites/favorites-page-link/favorites-page-link.component';

import { filterParams, layout, loadMoreProducts, loadQuickFilterFacets, selectedFacets } from '../../+state/products/products.actions';
import { ProductsFacade } from '../../+state/products/products.facade';
import { FeatureDetectionService } from '../../core/feature-detection.service';
import { MetaService } from '../../core/meta.service';
import { ScrollService } from '../../core/scroll.service';
import { FilterRootComponentModule } from '../../filter/filter-root/filter-root.component';
import { SpinnerModule } from '../../spinner/spinner.module';
import { MyProfileButtonModule } from '../my-profile-button/my-profile-button.component';
import { ProductListComponent } from '../product-list/product-list.component';
import { ProductSearchModule } from '../product-search/product-search.component';
import { ProductSortingComponent } from '../product-sorting/product-sorting.component';
import { ProductService } from '../product.service';

@Component({
    selector: 'ncg-filter-product-list',
    template: `
        <section class="product-list section" [ngClass]="filterPageLayout === 'filterVisible' ? 'product-list--full' : 'product-list--panel'">
            <div [ngClass]="{ container: filterPageLayout === 'filterInSidepanel' }">
                <div *ngIf="data.name && filterPageLayout === 'filterInSidepanel'" class="mb-4 pb-6 has-text-centered is-hidden-touch">
                    <h1>{{ data.name }}</h1>
                </div>
                <div *ngIf="filterPageLayout === 'filterInSidepanel'">
                    <ncg-filter-root [isPartOfFilterPage]="true" [showFilterButton]="true"></ncg-filter-root>
                </div>

                <div class="columns is-gapless">
                    <div class="column is-narrow product-list__aside is-hidden-touch" *ngIf="filterPageLayout === 'filterVisible'">
                        <div class="product-list__facets" #filterFacets>
                            <ncg-filter-facets></ncg-filter-facets>
                        </div>
                    </div>
                    <div class="column" [ngClass]="{ 'is-filter-padding': filterPageLayout === 'filterVisible' }">
                        <div *ngIf="data.name && filterPageLayout === 'filterVisible'" class="pt-6 is-hidden-touch level-left level-item">
                            <h1>{{ data.name }}</h1>
                        </div>
                        <ncg-product-sorting [title]="data.name"></ncg-product-sorting>
                        <div class="product-list__loading has-text-centered level level-item" *ngIf="isFetching">
                            <ncg-spinner [active]="isFetching"></ncg-spinner>
                        </div>
                        <div class="is-relative">
                            <ng-container #vc></ng-container>
                        </div>
                        <div class="product-list__bottom has-text-centered" *ngIf="currentTotal && itemsAmount">
                            <div>{{ 'product_list.progressbar' | translate: { amount: itemsAmount, total: currentTotal } }}</div>
                            <div class="product-list__progress">
                                <div class="product-list__bar" [ngStyle]="{ transform: 'scaleX(' + (itemsAmount / currentTotal) * 1 + ')' }"></div>
                            </div>
                            <a *ngIf="itemsAmount < currentTotal" class="button is-primary" (click)="loadMore($event)" [href]="nextPageUrl">
                                {{ 'product_list.load_more' | translate }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterProductListComponent implements OnInit, OnDestroy {
    @ViewChild('vc', { read: ViewContainerRef, static: true }) viewContainer: ViewContainerRef;
    @ViewChild('filterFacets') filterFacetsRef: ElementRef;
    @Input() data: IFilterPageResponse;
    readonly fromAmount = 0;
    readonly takeAmount = this.productService.takeAmount;
    componentRef: ComponentRef<ProductListComponent>;
    nextPageUrl: string = '';
    initialParams: any = {};
    itemsAmount? = 0;
    currentTotal? = 0;
    isFetching: boolean;
    filterPageLayout?: IFilterPageLayout;
    isLoadMoreClicked = false;
    buildIdQuery = getBuildIdQuery();

    private readonly unsubscribe = new Subject<void>();
    private isInitialLoad = true;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly metaService: MetaService,
        private readonly productService: ProductService,
        private readonly productsFacade: ProductsFacade,
        private readonly scrollService: ScrollService,
        private readonly translateService: TranslateService,
        private router: Router
    ) {}

    async ngOnInit() {
        const components = await import('../product-list/product-list.component');
        this.componentRef = this.viewContainer.createComponent(components.ProductListComponent);
        this.componentRef.instance.skeletonsAmount = this.takeAmount;
        this.initialParams = this.productService.getQueryParams();
        this.getProducts();
        this.dispatchLayout();
        this.dispatchFilterParams();
        this.getLayout();
        this.getTotal();
        this.getIsFetching();
        this.setMetaData();
    }

    dispatchLayout() {
        this.productsFacade.dispatch(
            layout({
                layout: {
                    pageLayout: this.data.layout,
                    multiSelectMakes: this.data.multiSelectMakes,
                    allowPremium: this.data.allowPremium,
                    showPremiumFacet: this.data.showPremiumFacet,
                    showUsedFacet: this.data.showUsedFacet,
                },
            })
        );
    }

    dispatchFilterParams() {
        if (this.data.layout === 'filterVisible') {
            this.productsFacade.dispatch(loadQuickFilterFacets());
        }

        const key = this.initialParams?.['make.keyword'] ?? this.data.preselectedMake;
        let queries: IFilterParams = {
            from: this.fromAmount.toString(),
            take: this.takeAmount.toString(),
        };

        if (key) {
            queries['make.keyword'] = [key];
        }

        if (this.isInitialLoad) {
            this.productService.setDefaultFilterParams(queries);
            queries = { ...queries, ...this.initialParams };
            this.isInitialLoad = false;
        }

        this.productsFacade.dispatch(filterParams({ filterParams: queries, resetFilter: true }));
    }

    getProducts() {
        this.productsFacade.allProducts$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.componentRef.instance.items = data;
            this.componentRef.instance.states.noItemsFound = data.length === 0;
            this.itemsAmount = data.length;
            this.componentRef.instance.ngOnChanges();
        });
    }

    getTotal() {
        this.productsFacade.total$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            this.currentTotal = data;
            this.cd.markForCheck();
        });
    }

    getLayout() {
        this.productsFacade.layout$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
            if (data) {
                this.filterPageLayout = data?.pageLayout;
                this.componentRef.instance.layout = data;
                this.componentRef.instance.ngOnChanges();
                this.cd.markForCheck();
            }
        });
    }

    getIsFetching() {
        this.productsFacade.isFetching$.pipe(takeUntil(this.unsubscribe)).subscribe((isFetching) => {
            if (isFetching) {
                this.scrollToTop();
            }
            this.isFetching = isFetching;
            this.componentRef.instance.states.isFetching = isFetching;
            this.componentRef.instance.ngOnChanges();
            this.cd.markForCheck();
        });
    }

    updateMetaTitle = async (amount: number, total: number) => {
        const translation =
            (await firstValueFrom(
                this.translateService.get('product_list.meta_title_prefix', {
                    amount,
                    total,
                })
            )) || '';
        const newTitle = [translation, this.data.meta.title].filter((x) => x).join(' | ');
        this.metaService.setTitle(newTitle);
    };

    setMetaData() {
        combineLatest([this.productsFacade.filterParams$, this.productsFacade.allProducts$, this.productsFacade.total$])
            .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe))
            .subscribe(([filterData, products, total]) => {
                const qp = this.productService.getQueryParams();
                const from = qp.from && typeof qp.from === 'string' ? parseInt(qp.from) : typeof qp.from === 'number' ? qp.from : 0;
                const take = qp.take && typeof qp.take === 'string' ? parseInt(qp.take) : typeof qp.take === 'number' ? qp.take : this.takeAmount;

                const { preselectedMake } = this.data;
                const amount = products.length;

                // Update nextPageUrl
                this.nextPageUrl = this.router.serializeUrl(
                    this.router.createUrlTree([], {
                        queryParams: {
                            ...qp,
                            from: from + take,
                            take: this.takeAmount,
                        },
                    })
                );

                // Update title for unique titles
                if (amount && total) {
                    this.updateMetaTitle(amount, total);
                }

                // Update canonical URL to include pagination for unique URLs
                // see more here https://developers.google.com/search/docs/specialty/ecommerce/pagination-and-incremental-page-loading#use-urls-correctly
                const newCanonicalUrl = this.router.serializeUrl(
                    this.router.createUrlTree([], {
                        queryParams: {
                            from: 0,
                            take: from + take,
                        },
                    })
                );
                this.metaService.setCanonical(newCanonicalUrl);

                // add robots noindex to avoid indexing
                // alternative sort orders
                // and filtered pages
                // see more here: https://developers.google.com/search/docs/specialty/ecommerce/pagination-and-incremental-page-loading#sequentially
                const filters = { ...qp };
                delete filters.from;
                delete filters.take;

                if (preselectedMake && filters['make.keyword'] === preselectedMake) {
                    delete filters['make.keyword'];
                }

                const hasSorting = !!filterData?.sort;
                const hasFilters = Object.keys(filters).length > 0;
                this.metaService.setNoIndex(hasSorting || hasFilters);
            });
    }

    scrollToTop() {
        if (this.featureDetectionService.isServer()) {
            return;
        }

        // Not necessary to scroll when filter is "hidden" in overlay
        if (this.filterPageLayout === 'filterInSidepanel') {
            return;
        }

        // Don't scroll to top when load more is clicked
        if (!this.isLoadMoreClicked) {
            this.scrollService.scrollToPosition({ top: 0 });
        }

        this.isLoadMoreClicked = false;
    }

    loadMore(event: MouseEvent) {
        event.preventDefault();

        if (this.isFetching) {
            return;
        }

        this.isLoadMoreClicked = true;
        this.initialParams = this.productService.getQueryParams();
        const fromNumber = Number(this.initialParams?.from) ?? 0;
        const currentNumber = Number(this.initialParams?.take) ?? this.takeAmount;
        const params: IFilterParams = {
            ...this.initialParams,
            from: (fromNumber + currentNumber).toString(),
            take: this.takeAmount.toString(),
        };

        this.productsFacade.dispatch(loadMoreProducts({ filterParams: params }));
    }

    ngOnDestroy() {
        this.productsFacade.dispatch(selectedFacets({ selectedFacets: [] }));
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}

@NgModule({
    declarations: [FilterProductListComponent, ProductSortingComponent],
    imports: [
        CommonModule,
        TranslateModule.forChild(),
        FormsModule,
        NgbDropdownModule,
        ProductSearchModule,
        MyProfileButtonModule,
        FilterRootComponentModule,
        SpinnerModule,
        IconSpriteModule,
        FavoritesPageLinkComponent,
    ],
})
export class FilterProductListComponentModule {}
